import React, { useCallback, useRef, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { PlaidLinkOnExit } from 'react-plaid-link';

import { PlaidWrapper } from './steps/PlaidButton/PlaidButton';

import schoolBus from 'media/images/school-bus.jpg';
import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { colors } from 'styles/settings';
import { Organization } from 'common/api/crm/models/Organization';
import { IS_FLIPGIVE } from 'common/env';
import { getPath, Routes } from 'routes';
import { deleteOrgAchAccount } from 'common/api/e-comm/ach';
import { PlaidErrorCode } from 'common/utils/plaidVerification';
import { LoadingOverlay, openLoader, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { useRootSelector } from 'rootStore';
import { EmailCheckSection } from 'features/coordinator/components/EmailCheckSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { updateEnrollmentProgress } from 'common/api/crm/organization/enrollment';
import { ProgressState } from 'common/api/crm/enums/ProgressState';

interface OrgEnrollmentCompletedProps {
    organization: Organization;
    curatedArticles: unknown[];
    status?: string;
}

export interface OrgEnrollmentCompletedParams {
    status?: 'deposits' | 'paper-only' | 'link-bank-later' | 'voided-check-completed';
}

const styles = createStyles({
    wrapper: 'flex flex-col p-0 mx-0 max-w-full sm:mx-auto sm:max-w-screen-xl mt-0',
    subHeader: 'relative pt-0 pb-3 font-light sm:flex sm:pt-0 sm:pb-4',
    titleInfo:
        'w-full h-80 sm:h-96 z-10 absolute sm:relative sm:bg-brand-accent/90 sm:w-3/5 flex flex-col word-wrap justify-center align-start bg-brand-accent/80 text-3xl text-white px-8 lg:pl-20 ',
    image: 'w-full h-80 sm:h-96 bg-no-repeat bg-center bg-cover absolute sm:relative sm:w-2/5 ',
    subTitle: 'text-3xl sm:text-5xl text-brand-dark font-light text-left',
    icon: 'mt-0 mb-0 sm:mt-6 sm:mb-4 sm:px-0 px-2 text-brand h-10 sm:h-16',
    contentWrapper:
        'max-w-full relative mt-80 sm:mt-0 sm:max-w-3xl mx-auto px-8 text-lg text-grey-1 lg:px-0',
    button: [
        'sm:flex-col w-full mb-8 sm:mb-0 text-left sm:text-center',
        {
            borderColor: colors.grey5,
            '@media screen and (max-width: 640px)': {
                justifyContent: 'flex-end',
                flexDirection: 'row-reverse',
                padding: 0,
                height: 70,
            },
        },
    ],
});

interface BaseViewProps {
    titleInfo: React.ReactNode;
    nextSteps: React.ReactNode;
}

const BaseView = ({ titleInfo, nextSteps }: BaseViewProps) => {
    const getStartedUrl = IS_FLIPGIVE
        ? 'https://www.flipgive.com/resources/organization/program-headstart/'
        : 'https://www.raiseright.com/resources/organization/program-headstart/';
    return (
        <div className={styles.wrapper}>
            <div className={styles.subHeader}>
                <div
                    style={{
                        backgroundImage: `linear-gradient(#00000090, #00000045), url(${schoolBus})`,
                    }}
                    className={styles.image}
                />
                <div className={styles.titleInfo}>{titleInfo}</div>
            </div>

            <div className={styles.contentWrapper}>
                <div className="mb-6 sm:mb-10" />
                <h1 tabIndex={0} className={styles.subTitle}>
                    What&apos;s next?
                </h1>
                <br />
                {nextSteps}
                <div className="mb-6 sm:mb-16" />
                <h2 className={styles.subTitle}>Want to get a head start?</h2>
                <br />
                <p>
                    While you wait for your approval, visit our{' '}
                    <a href={getStartedUrl} target="_blank" rel="noopener noreferrer">
                        Get Started
                    </a>{' '}
                    page for information on next steps to take to get your program up and running.
                </p>
                <br />
                <p>
                    Need help or have questions? Don&apos;t hesitate to reach out to our team at
                    800-727-4715 (Option 4). We&apos;re here to support you.
                </p>
            </div>
            <div className="mb-6 sm:mb-16" />
        </div>
    );
};

const PaperOnlyView = ({ organization }: OrgEnrollmentCompletedProps) => {
    const home = useRootSelector((s) => s.store.home);
    const firstName = home.homeDetails?.profile?.firstName;
    const ref = useRef<LoadingOverlay>(null);
    useHomeDetails();

    useEffect(() => {
        if (home.homeDetailsAsyncState.loading) {
            openLoader(ref.current);
        } else {
            setTimeout(() => {
                closeLoader(ref.current);
            }, 500);
        }
    }, [home.homeDetailsAsyncState.loading]);

    const TitleInfo = (
        <div>
            <span className="text-4xl lg:pr-64 font-light">
                You&apos;re on your way{firstName ? ` ${firstName}` : ''}.
            </span>
            <LoadingOverlay ref={ref} />
            <br />
            <h3 className="font-light sm:font-bold mt-8">Now it&apos;s on us.</h3>
        </div>
    );

    const emailAddress = IS_FLIPGIVE ? 'fundraising@flipgive.com' : 'GetStarted@RaiseRight.com';

    const onVoidedCheckStepSelected = async () => {
        await updateEnrollmentProgress({
            state: ProgressState.VoidedCheckSelected,
            organizationId: organization.id,
        });
    };

    const WhatsNext = (
        <div>
            <p>
                Thank you for submitting the enrollment form! If you provided all required
                information, you&apos;re all set—we&apos;ll begin reviewing your submission shortly.
            </p>
            <p className="mt-4">
                If you skipped any of the following items, please email us at{' '}
                <a href={`mailto:${emailAddress}`}>{emailAddress}</a> to complete your enrollment:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Your role or title within the organization</li>
                <li>A link to your official website or social media profile</li>
            </ul>
            <p className="mt-4">
                This information is essential for getting your organization approved and ready to
                start earning.
            </p>
            <div className="flex rounded-xl border mt-10">
                <div className="p-1 w-48 rounded-xl border-r bg-brand-light justify-center items-center text-center">
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        size="4x"
                        className="mt-10 text-brand-dark"
                    />
                </div>
                <div>
                    <p className="p-2">
                        Organizations that connect a bank account earn twice as much as other
                        programs. Maximize your earning potential with online ordering, instant
                        eGift card delivery, and more.
                    </p>
                    <p className="text-center">
                        <Link
                            onClick={onVoidedCheckStepSelected}
                            to={Routes.StartProgramVoidedCheck}
                            className="text-xs"
                        >
                            {IS_FLIPGIVE
                                ? 'Upload or email a voided cheque'
                                : 'Upload or email a voided check'}
                        </Link>
                    </p>
                    <p className="text-xs mt-2 p-2">
                        Need Support? We&apos;re ready to help if you need any assistance. (800)
                        727-4715 | Option 4
                    </p>
                </div>
            </div>
        </div>
    );

    return <BaseView titleInfo={TitleInfo} nextSteps={WhatsNext} />;
};

const VoidedCheckCompletedView = (_props: OrgEnrollmentCompletedProps) => {
    const home = useRootSelector((s) => s.store.home);
    const firstName = home.homeDetails?.profile?.firstName;
    const ref = useRef<LoadingOverlay>(null);
    useHomeDetails();

    useEffect(() => {
        if (home.homeDetailsAsyncState.loading) {
            openLoader(ref.current);
        } else {
            setTimeout(() => {
                closeLoader(ref.current);
            }, 500);
        }
    }, [home.homeDetailsAsyncState.loading]);

    const TitleInfo = (
        <div>
            <span className="text-4xl lg:pr-64 font-light">
                You&apos;re on your way{firstName ? ` ${firstName}` : ''}.
            </span>
            <LoadingOverlay ref={ref} />
            <br />
            <h3 className="font-light sm:font-bold mt-8">Now it&apos;s on us.</h3>
        </div>
    );

    const WhatsNext = (
        <div>
            <p>
                Thank you for submitting the enrollment form! If you provided all required
                information, you&apos;re all set—we&apos;ll begin reviewing your submission shortly.
            </p>
            <p className="mt-4">
                If you skipped any of the following items, please email us at{' '}
                <a href="mailto:fundraising@flipgive.com">Fundraising@FlipGive.com</a> to complete
                your enrollment:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Your role or title within the organization</li>
                <li>A link to your official website or social media profile</li>
            </ul>
            <p className="mt-4">
                This information is essential for getting your organization approved and ready to
                start earning.
            </p>
        </div>
    );

    return <BaseView titleInfo={TitleInfo} nextSteps={WhatsNext} />;
};

const LinkBankLaterView = (_props: OrgEnrollmentCompletedProps) => {
    const home = useRootSelector((s) => s.store.home);
    const firstName = home.homeDetails?.profile?.firstName;
    const ref = useRef<LoadingOverlay>(null);
    useHomeDetails();

    useEffect(() => {
        if (home.homeDetailsAsyncState.loading) {
            openLoader(ref.current);
        } else {
            setTimeout(() => {
                closeLoader(ref.current);
            }, 500);
        }
    }, [home.homeDetailsAsyncState.loading]);

    const TitleInfo = (
        <div>
            <span className="text-4xl lg:pr-64 font-light">
                You&apos;re on your way{firstName ? ` ${firstName}` : ''}.
            </span>
            <LoadingOverlay ref={ref} />
            <br />
            <h3 className="font-light sm:font-bold mt-8">Now it&apos;s on us.</h3>
        </div>
    );

    const WhatsNext = (
        <div>
            <p>
                Thank you for submitting the enrollment form! If you provided all required
                information, you&apos;re all set—we&apos;ll begin reviewing your submission shortly.
            </p>
            <p className="mt-4">
                If you skipped any of the following items, please email us at{' '}
                <a href="mailto:fundraising@flipgive.com">Fundraising@FlipGive.com</a> to complete
                your enrollment:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Your role or title within the organization</li>
                <li>A link to your official website or social media profile</li>
            </ul>
            <p className="mt-4">
                This information is essential for getting your organization approved and ready to
                start earning.
            </p>
        </div>
    );

    return <BaseView titleInfo={TitleInfo} nextSteps={WhatsNext} />;
};

const DepositsView = ({ organization, status }: OrgEnrollmentCompletedProps) => {
    const home = useRootSelector((s) => s.store.home);
    const firstName = home.homeDetails?.profile?.firstName;
    const ref = useRef<LoadingOverlay>(null);
    useHomeDetails();
    const history = useHistory();
    const { id: orgId = '' } = organization;

    useEffect(() => {
        if (home.homeDetailsAsyncState.loading) {
            openLoader(ref.current);
        } else {
            setTimeout(() => {
                closeLoader(ref.current);
            }, 500);
        }
    }, [home.homeDetailsAsyncState.loading]);

    const onMicroDepositDone = useCallback(async () => {
        history.replace(getPath(Routes.StartProgramCompleted, {}));
    }, [history]);

    const onPlaidExit: PlaidLinkOnExit = useCallback(
        async (error) => {
            if (orgId && error && error.error_code === PlaidErrorCode.TooManyVerificationAttempts) {
                await deleteOrgAchAccount(orgId);
                history.replace(Routes.StartProgramEarnings);
            }
        },
        [orgId, history]
    );

    const depositsEmailAddress = IS_FLIPGIVE
        ? 'fundraising@flipgive.com'
        : 'GetStarted@RaiseRight.com';

    const TitleInfo = (
        <div>
            <span className="text-4xl lg:pr-64 font-light">
                You&apos;re on your way{firstName ? ` ${firstName}` : ''}.
            </span>
            <LoadingOverlay ref={ref} />
            <br />
            <h3 className="font-light sm:font-bold mt-8">Now it&apos;s on us.</h3>
        </div>
    );

    const WhatsNext = (
        <>
            <p>
                Thank you for submitting the enrollment form! If you provided all required
                information, you&apos;re all set—we&apos;ll begin reviewing your submission shortly.
            </p>
            <p className="mt-4">
                If you skipped any of the following items, please email us at{' '}
                <a href={`mailto:${depositsEmailAddress}`}>{depositsEmailAddress}</a> to complete
                your enrollment:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Your role or title within the organization</li>
                <li>A link to your official website or social media profile</li>
            </ul>
            <p className="mt-4">
                This information is essential for getting your organization approved and ready to
                start earning.
            </p>
            {status === 'deposits' && (
                <>
                    <br />
                    <p>
                        A $0.01 deposit from Plaid should appear in your bank account after 1-2
                        business days. Return here to complete your setup by entering the code.
                        (Hint: your code is the first 3 letters after the #.)
                    </p>
                    <br />
                    <PlaidWrapper
                        orgId={orgId}
                        onSuccess={onMicroDepositDone}
                        onExit={onPlaidExit}
                        isResuming
                    >
                        {(openPlaid, isPlaidReady) => (
                            <Btn
                                disabled={!isPlaidReady}
                                onClick={openPlaid}
                                className="w-full sm:w-auto"
                            >
                                Complete Setup
                            </Btn>
                        )}
                    </PlaidWrapper>
                    <EmailCheckSection />
                </>
            )}
        </>
    );

    return <BaseView titleInfo={TitleInfo} nextSteps={WhatsNext} />;
};

export const OrgEnrollmentCompleted = (props: OrgEnrollmentCompletedProps) => {
    const { status } = useParams<OrgEnrollmentCompletedParams>();
    const View =
        status === 'voided-check-completed'
            ? VoidedCheckCompletedView
            : status === 'link-bank-later'
            ? LinkBankLaterView
            : status === 'paper-only'
            ? PaperOnlyView
            : DepositsView;

    useEffect(() => {
        localStorage.removeItem('link-token');
    }, []);

    return (
        <View
            status={status}
            organization={props.organization}
            curatedArticles={props.curatedArticles}
        />
    );
};
